// Geführte Fotoaufnahme: Tür → Flur abschnittsweise → Raum für Raum ringsum
// → große Schränke geöffnet.
//
// Seit 08.10.2026 (Live-Durchlauf: 18 Rückfragen bei 9 Räumen) weniger fragen:
//   * Schrankfrage nur, wo große Schränke typisch sind (CABINET_ROOM_TYPES),
//     mit einem klaren Weg: "Ja" → offene Schränke fotografieren, "Nein" →
//     Raum fertig. Die Antwort steht am Raum (rooms.cabinets).
//   * "Raum fertig" bestätigt den Raum direkt (keine Nachfrage "vollständig?").
//   * Das Türfoto wird einmal erbeten; die Prüfseite fragt nicht noch einmal.
//
// Seit 15.10.2026 nach Wänden (ui/Waende.tsx): feste Reihenfolge ab der Zimmertür, Kurzform für kleine Räume.
// Ältere Räume mit freien Rundum-Fotos behalten die bisherige Kamera.
//
// Seit 30.10.2026 (Betreiber: „alle Schritte pragmatisch vereinfachen“) eine Seite je Raum: Wände, freiwillige Karte
// „Große Schränke geöffnet“ und aufklappbar weitere Türen. Die Schrankfrage und die Türseite entfallen dort.

import { useState } from "react";
import { CABINET_ROOM_TYPES, HALL_SIDE_LABEL, ROOM_TYPE_LABEL, ROOM_WALL_LABEL, floorLabel, type PhotoStep } from "@umzugsscan/shared";
import { GUIDE_TEXT, ROOM_HINT, guideSteps, hasFloorPlan } from "../ablauf.ts";
import { useDraft, useStore } from "../hooks.ts";
import { Vorschau, qualityIssue } from "../ui/Bild.tsx";
import { Button, Dialog, Footer } from "../ui/elemente.tsx";
import { Fotohinweis } from "../ui/Fotohinweis.tsx";
import { Kamera } from "../ui/Kamera.tsx";
import { FotoReihe } from "../ui/Galerie.tsx";
import { RaumTueren } from "../ui/RaumTueren.tsx";
import { RaumWaende, STEP_TITLE, photoModeOf, requiredSteps } from "../ui/Waende.tsx";

export function Fotos() {
  const d = useDraft();
  const store = useStore();
  const steps = guideSteps(d.rooms);
  const { guide, phase, returnToCheck } = d.nav;
  const step = steps[Math.min(guide, steps.length - 1)];
  const [dialog, setDialog] = useState<null | "leer" | "tuer" | "waende">(null);
  const [busy, setBusy] = useState(false);
  const planned = hasFloorPlan(d.rooms);
  const start = planned ? "grundriss" : "raeume";

  if (step && !d.survey.photoNotice) return <Fotohinweis onBack={() => store.go({ screen: start })} />;

  if (!step) {
    return (
      <>
        <h1>Noch keine Räume</h1>
        <p>Bitte wählen Sie zuerst aus, welche Räume Ihre Wohnung hat.</p>
        <Footer onNext={() => store.go({ screen: planned ? "grundriss" : "eingang" })} nextLabel="Räume wählen" />
      </>
    );
  }

  const room = d.rooms.find((r) => r.id === step.roomId)!;
  const mode = step.kind === "raum" ? photoModeOf(room, d.photos) : null;
  const wallsView = step.kind === "raum" && mode !== null;
  // Schrankfrage und eigener Schrankschritt nur noch für ältere Räume mit freien Rundum-Fotos.
  const asksCabinets = step.kind === "raum" && !wallsView && CABINET_ROOM_TYPES.includes(room.type);
  const inCabinets = asksCabinets && phase === "schrank";
  const photoStep: PhotoStep = step.kind === "tuer" ? "tuer" : step.kind === "flur" ? "flur" : inCabinets ? "schrank_offen" : "rundum";
  const photos = d.photos.filter((p) => p.roomId === room.id && p.step === photoStep);
  const roomPhotos = d.photos.filter((p) => p.roomId === room.id && p.step !== "tuer");
  // Schrankschritt: erst die Frage (ein klarer Weg), nach "Ja" die Kamera.
  const showCabinetQuestion = inCabinets && room.cabinets !== "ja" && photos.length === 0;
  const text = step.kind === "raum" ? GUIDE_TEXT[inCabinets ? "schrank" : "rundum"] : GUIDE_TEXT[step.kind];
  const issues = photos.filter((p) => qualityIssue(p)).length;
  const parent = room.hallRoomId ? d.rooms.find((r) => r.id === room.hallRoomId) : undefined;
  const missingWalls = wallsView ? requiredSteps(mode!, room.roomShape).filter((st) => !roomPhotos.some((p) => p.step === st)) : [];

  const goTo = (g: number, ph: "rundum" | "schrank" = "rundum") => {
    if (g >= steps.length) store.go({ screen: returnToCheck ? "pruefung" : "besonderes", returnToCheck: false });
    else store.go({ guide: g, phase: ph });
  };
  const finishRoom = () => {
    store.confirmRoom(room.id);
    setDialog(null);
    if (returnToCheck) store.go({ screen: "pruefung", returnToCheck: false });
    else goTo(guide + 1);
  };

  const next = () => {
    if (step.kind === "tuer") {
      // Einmal erbeten; "Ohne Foto weiter" wird später nicht noch einmal angemahnt.
      if (photos.length === 0) setDialog("tuer");
      else goTo(guide + 1);
    } else if (roomPhotos.length === 0) {
      setDialog("leer");
    } else if (missingWalls.length > 0 && dialog !== "waende") {
      setDialog("waende");
    } else if (step.kind === "raum" && phase === "rundum" && asksCabinets) {
      setDialog(null);
      goTo(guide, "schrank");
    } else {
      finishRoom();
    }
  };
  const back = () => {
    if (inCabinets) goTo(guide, "rundum");
    else if (guide === 0) store.go({ screen: start });
    else {
      const prev = steps[guide - 1];
      const prevRoom = prev && d.rooms.find((r) => r.id === prev.roomId);
      const legacyCabinets = prev?.kind === "raum" && !!prevRoom && !photoModeOf(prevRoom, d.photos) && CABINET_ROOM_TYPES.includes(prevRoom.type);
      goTo(guide - 1, legacyCabinets ? "schrank" : "rundum");
    }
  };

  const roomNo = steps.filter((s) => s.kind !== "tuer").findIndex((s) => s.roomId === room.id) + 1;
  const roomTotal = steps.filter((s) => s.kind !== "tuer").length;
  const floors = new Set(d.rooms.map((r) => r.floor ?? 0));
  const here = room.name === ROOM_TYPE_LABEL[room.type] ? "diesem Raum" : room.name;

  return (
    <>
      <p className="eyebrow">
        {step.kind === "tuer" ? "Start" : `Raum ${roomNo} von ${roomTotal}`} · {room.name}
        {planned && floors.size > 1 && ` · ${floorLabel(room.floor ?? 0)}`}
      </p>
      {planned && room.hallSide && !inCabinets && <p className="hint">Die Tür im Flur {HALL_SIDE_LABEL[room.hallSide]}.</p>}
      {parent && parent.type !== "flur" && !inCabinets && (
        <p className="hint">
          Erreichbar über {parent.name}
          {room.doorWall ? ` (${ROOM_WALL_LABEL[room.doorWall]})` : ""}.
        </p>
      )}

      {showCabinetQuestion ? (
        <>
          <h1>Gibt es in {here} große Schränke?</h1>
          <p className="lead">Zum Beispiel Kleiderschrank, Schrankwand oder Einbauschrank. Dann fotografieren Sie sie bitte einmal geöffnet – so können wir die Zahl der Kartons einschätzen.</p>
          <div className="stack">
            <Button onClick={() => store.setCabinets(room.id, "ja")}>Ja, geöffnet fotografieren</Button>
            <Button
              variant="secondary"
              onClick={() => {
                store.setCabinets(room.id, "nein");
                finishRoom();
              }}
            >
              Nein – Raum fertig
            </Button>
          </div>
          <Footer onBack={back} />
        </>
      ) : (
        <>
          <h1>{wallsView ? (mode === "kurz" ? `${room.name}: zwei Fotos` : `${room.name}: Wand für Wand`) : text.title}</h1>
          <p className="lead">
            {wallsView
              ? mode === "kurz"
                ? "Ein kleiner Raum braucht nur zwei Fotos: aus der Tür hinein und aus der hintersten Ecke zurück."
                : "Immer in derselben Reihenfolge, so können wir Ihre Wohnung genau aufzeichnen: aus der Tür die Wand gegenüber, die rechte und die linke Wand, zum Schluss die Wand mit der Tür. Alle Möbel ganz im Bild, Licht an."
              : text.text}
          </p>
          {step.kind === "raum" && !inCabinets && ROOM_HINT[room.type] && <p className="tip">{ROOM_HINT[room.type]}</p>}
          {wallsView ? (
            <>
              <RaumWaende room={room} onBusy={setBusy} />
              <RaumTueren room={room} />
            </>
          ) : (
            <Kamera roomId={room.id} step={photoStep} single={step.kind === "tuer"} onBusy={setBusy} />
          )}
          {!wallsView && photos.length > 0 && (
            <>
              <FotoReihe photos={photos} onDelete={(p) => store.deletePhoto(p.id)} />
              <p className="hint">
                {photos.length} Foto{photos.length === 1 ? "" : "s"}
                {issues > 0 && ` · ${issues} bitte neu aufnehmen (× löschen, dann neu fotografieren)`}
              </p>
            </>
          )}
          <Footer
            onBack={back}
            onNext={next}
            nextDisabled={busy}
            nextLabel={
              step.kind === "tuer"
                ? steps[guide + 1]?.kind === "flur"
                  ? "Weiter zum Flur"
                  : "Weiter"
                : phase === "rundum" && asksCabinets
                  ? "Weiter"
                  : "Raum fertig"
            }
          />
        </>
      )}

      {dialog === "leer" && (
        <Dialog
          title={`Noch kein Foto: „${room.name}“`}
          onClose={() => setDialog(null)}
          actions={
            <>
              <Button onClick={() => setDialog(null)}>Foto aufnehmen</Button>
              <Button
                variant="quiet"
                onClick={() => {
                  setDialog(null);
                  goTo(guide + 1);
                }}
              >
                Später nachholen
              </Button>
            </>
          }
        >
          <p>Ohne Fotos können wir diesen Raum nicht einschätzen.</p>
        </Dialog>
      )}
      {dialog === "waende" && (
        <Dialog
          title={`Noch nicht vollständig: „${room.name}“`}
          onClose={() => setDialog(null)}
          actions={
            <>
              <Button onClick={() => setDialog(null)}>Foto aufnehmen</Button>
              <Button variant="quiet" onClick={next}>
                Trotzdem weiter
              </Button>
            </>
          }
        >
          <p>Es fehlt noch: {missingWalls.map((st) => STEP_TITLE[st]).join(", ")}. Ohne diese Fotos übersehen wir leicht Möbel.</p>
        </Dialog>
      )}
      {dialog === "tuer" && (
        <Dialog
          title="Noch kein Foto vom Eingang"
          onClose={() => setDialog(null)}
          actions={
            <>
              <Button onClick={() => setDialog(null)}>Foto aufnehmen</Button>
              <Button
                variant="quiet"
                onClick={() => {
                  setDialog(null);
                  goTo(guide + 1);
                }}
              >
                Ohne Foto weiter
              </Button>
            </>
          }
        >
          <p>Das Foto vom Eingang hilft, den Weg aus der Wohnung einzuschätzen. Es ist freiwillig.</p>
        </Dialog>
      )}
    </>
  );
}
